import { Component, input, inject } from '@angular/core';
import { Process } from '../../data/process';
import { ProcessField } from '../../data/process-field';
import { EmployeeType } from '../../types/employee-type';
import { UtilService } from '../../services/util.service';
import { FieldType } from '../../types/field-type';
import { ActionType } from '../../types/action-type';
import { ExportService } from '@rbb/ng-web-template-lib';
import { NgSwitch } from '@angular/common';

@Component({
  selector: 'app-process-preview',
  templateUrl: './process-preview.component.html',
  styleUrls: ['./process-preview.component.css'],
  imports: [NgSwitch]
})
export class ProcessPreviewComponent {
  private utilService = inject(UtilService);
  private exportService = inject(ExportService);

  readonly process = input<Process>();

  readonly onlyRelevanteFields = input<boolean>();

  contentToHTML(field: ProcessField): string {
    if (field.formField?.type === 'EMPLOYEE_TYPE') {
      return this.mapEmployeeType(field.content);
    }
    if (!field.content && field.content !== 0) {
      return '--';
    }
    if (field.formField?.type === 'DATE') {
      return this.utilService.escapeHtmlWithLink(field.content.substring(8, 10) + '.' + field.content.substring(5, 7) + '.' + field.content.substring(0, 4)) ?? '';
    }

    if (field.formField?.type === 'PERSON') {
      return this.utilService.processStyleMarkup(field.content) ?? '';
    }

    if (field.formField?.type === 'PERSON_LIST' || field.formField?.type === 'PRODUCTION_UNIT') {
      let json;
      let str = '<ul>';
      if (Array.isArray(field.content)) {
        json = field.content;
      } else {
        json = JSON.parse(field.content);
      }
      if (Array.isArray(json)) {
        if (json.length === 0) {
          return '--';
        }
        for (const person of json) {
          str += '<li>' + this.utilService.processStyleMarkup(person) + '</li>';
        }
      }
      if (str === '<ul>') {
        return '--';
      }
      str += '</ul>';
      return str;
    }

    if (field.formField?.type === 'SELECTION_TABLE') {
      let str = '<table class="table table-sm"><thead><tr><th></th>';
      for (const option of field.formField.fieldCustomParameter.options) {
        str += '<th class="text-center">' + this.utilService.processStyleMarkup(option) + '</th>';
      }
      str += '</tr></thead><tbody>';
      let json;
      if (typeof field.content === 'object') {
        json = field.content;
      } else {
        json = JSON.parse(field.content);
      }
      for (const item of field.formField.fieldCustomParameter.items) {
        str += '<tr><td>' + this.processStyleMarkup(item) + '</td>';
        for (const option of field.formField.fieldCustomParameter.options) {
          str += '<td class="text-center">';
          if (json[item]?.includes(option)) {
            str += 'X';
          }
          str += '</td>';
        }
        str += '</tr>';
      }
      str += '</tbody></table>';
      return str;
    }

    let str = '';
    try {
      let json;
      if (Array.isArray(field.content)) {
        json = field.content;
      } else {
        json = JSON.parse(field.content);
      }
      if (Array.isArray(json)) {
        if (json.length === 0) {
          return '--';
        }
        if (Array.isArray(json[0])) {
          str += '<table class="table table-sm"><thead><tr>';
          for (const header of json[0]) {
            str += '<th>' + this.utilService.escapeHtmlWithLink(header) + '</th>';
          }
          str += '</tr></thead><tbody>';
          for (let i = 1; i < json.length; i++) {
            let sumRow = false;
            if (i === json.length - 1) {
              if (json[i].filter((s: string) => s !== '--' && !s.startsWith('Σ ')).length === 0) {
                sumRow = true;
              }
            }
            str += sumRow ? '<tr class="sum-row fw-bold">' : '<tr>';
            for (const cell of json[i]) {
              str += '<td>' + this.utilService.escapeHtmlWithLink(cell) + '</td>';
            }
            str += '</tr>';
          }
          str += '</tbody></table>';
        } else {
          if (json.length === 1) {
            str += this.utilService.escapeHtmlWithLink(json[0].trim());
          } else {
            str += '<ul>';
            for (const el of json) {
              if (el.includes('Summe ->')) {
                str += '<li class="fw-bold">' + this.utilService.escapeHtmlWithLink(el.trim()) + '</li>';
              } else {
                str += '<li>' + this.utilService.escapeHtmlWithLink(el.trim()) + '</li>';
              }
            }
            str += '</ul>';
          }
        }
      } else {
        str = this.utilService.escapeHtmlWithLink(field.content.toString().trim())?.replaceAll('\n', '<br>') ?? '';
      }
    } catch {
      return this.utilService.escapeHtmlWithLink(field.content.toString().trim())?.replaceAll('\n', '<br>') ?? '';
    }
    return str;
  }

  mapEmployeeType(employeeType: EmployeeType): string {
    let s = 'Unbekannt';
    switch (employeeType) {
      case EmployeeType.PERMANENT:
        s = 'Fest';
        break;
      case EmployeeType.FREELANCER:
        s = 'Frei';
        break;
      case EmployeeType.TEMPORARY:
        s = 'Aushilfe';
        break;
      case EmployeeType.EXTERNAL:
        s = 'Extern';
        break;
    }
    return s;
  }

  processStyleMarkupWithoutBreaks(s: string | null | undefined): string | null | undefined {
    return s ? this.utilService.processStyleMarkupWithoutBreaks(s) : '';
  }

  processStyleMarkup(s: string | null | undefined): string | null | undefined {
    return s ? this.utilService.processStyleMarkup(s) : '';
  }

  parseJson(json: string | { name: string; mimeType: string; data: string }[]): { name: string; mimeType: string; data: string }[] {
    if (typeof json == 'string') {
      return JSON.parse(json);
    } else {
      return json;
    }
  }

  openFile(name: string, mimeType: string, data: string): void {
    const byteCharacters = atob(data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    this.exportService.createDownloadableFile(mimeType, name, byteArray);
  }

  leadingZero(n: number): string {
    return n < 10 ? '0' + n.toString() : n.toString();
  }

  defaultHeader(): boolean {
    const process = this.process();
    return !!process?.fields && process.fields.length > 0 && process.fields[0].formField?.type !== 'HEADER';
  }

  protected readonly FieldType = FieldType;
  protected readonly ActionType = ActionType;
}
